type ClarityApi = {
  init: (projectId: string) => void;
  identify?: (customUserId: string) => void;
};

const CLARITY_DISABLE_TOKENS = new Set(['false', '0', 'no', 'off', 'disabled']);

function isClarityEnabled(): boolean {
  // Opt-out only: enabled unless explicitly set to a false token.
  const rawFlag = import.meta.env.VITE_ENABLE_CLARITY;
  if (rawFlag === undefined) return true;
  const normalized = String(rawFlag).trim().toLowerCase();
  return !CLARITY_DISABLE_TOKENS.has(normalized);
}

function getClarityProjectId(): string | null {
  const raw = import.meta.env.VITE_CLARITY_PROJECT_ID;
  if (raw === undefined) return null;
  const trimmed = String(raw).trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isClarityInitialized(): boolean {
  return window.__ieHubClarityInitialized === true;
}

function markClarityInitialized(): void {
  window.__ieHubClarityInitialized = true;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

let clarityApiPromise: Promise<ClarityApi | null> | null = null;

async function loadClarityApi(): Promise<ClarityApi | null> {
  if (clarityApiPromise) return clarityApiPromise;
  clarityApiPromise = (async () => {
    try {
      const mod = await import('@microsoft/clarity');
      const candidate: unknown =
        isObject(mod) && 'default' in mod ? (mod as { default?: unknown }).default ?? mod : mod;

      if (!isObject(candidate)) return null;

      const init = candidate.init;
      const identify = candidate.identify;

      if (typeof init !== 'function') return null;
      if (identify !== undefined && typeof identify !== 'function') return null;

      return {
        init: init as ClarityApi['init'],
        identify: identify as ClarityApi['identify'],
      };
    } catch {
      return null;
    }
  })();
  return clarityApiPromise;
}

export async function initClarity(): Promise<void> {
  try {
    const projectId = getClarityProjectId();
    if (!projectId) return;
    if (!isClarityEnabled()) return;
    if (isClarityInitialized()) return;

    const Clarity = await loadClarityApi();
    if (!Clarity) return;

    Clarity.init(projectId);
    markClarityInitialized();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Clarity init failed', e);
  }
}

/**
 * Toggle full-page masking for Microsoft Clarity session recordings.
 *
 * Sets/removes `data-clarity-mask` on <body> (not the React root) so the mask
 * also covers MUI dialogs, menus, selects and date pickers, which render via
 * portals outside the app tree. Clarity still records clicks, scrolls and
 * heatmaps — only the text content is blanked, so the admin team never sees
 * customer PII. Safe to call before Clarity has loaded; the attribute is read
 * by the recorder whenever it next snapshots/observes the DOM.
 */
export function setClarityMaskAll(enabled: boolean): void {
  try {
    if (typeof document === 'undefined' || !document.body) return;
    if (enabled) {
      document.body.setAttribute('data-clarity-mask', 'true');
    } else {
      document.body.removeAttribute('data-clarity-mask');
    }
  } catch {
    // Never let masking bookkeeping break rendering.
  }
}

export async function identifyClarityUser(
  user: { userId?: number | string | null | undefined } | number | string | null | undefined
): Promise<void> {
  try {
    if (!isClarityInitialized()) {
      await initClarity();
      if (!isClarityInitialized()) return;
    }

    const userId =
      typeof user === 'object' && user !== null ? user.userId : user;

    if (userId === null || userId === undefined) return;
    const id = String(userId).trim();
    if (!id) return;

    const Clarity = await loadClarityApi();
    if (!Clarity?.identify) return;

    // No PII: use userId only.
    Clarity.identify(id);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Clarity identify failed', e);
  }
}
